import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiQuery } from '../../core/api/entities/api-query.entity';
import { ShAbstractHttpService } from '../../core/http/services/http.abstract.service';
import { FacetProfileField } from '../../core/profiles/entities/profile-field.entity';
import { ShFacet } from '../../elements/components/facet/entities/facet.entity';
import { FacetRequest, FacetValueConstraint, FacetValueOptions } from '../entities/facet-request.entity';
import { Facet } from '../entities/facet.entity';
import { FacetsResponseToShFacets } from '../helpers/facet-response.helper';

@Injectable({ providedIn: 'root' })
export class ShFacetDataService {
    protected FACETS_ENDPOINT = 'v2/records/facets';

    constructor(protected httpService: ShAbstractHttpService) {}

    public getFacets(facetRequest: FacetRequest): Observable<Facet[]> {
        return this.httpService.post(this.FACETS_ENDPOINT, facetRequest);
    }

    // #region Get Facets by Profile

    /**
     * Get all facets of a profile
     * @param query
     * @param profileId
     * @param facetProfileFields
     * @returns
     */
    public getProfileFacets(
        query: ApiQuery,
        profileId: string,
        facetProfileFields: FacetProfileField[]
    ): Observable<ShFacet[]> {
        const facetRequest: FacetRequest = this.getProfileFacetRequest(query, profileId);

        return this.getFacets(facetRequest).pipe(map(facets => FacetsResponseToShFacets(facets, facetProfileFields)));
    }

    // #endregion

    // #region Get Facets by ProfileField
    /**
     * Get Facets of multiple profiles
     * @param query
     * @param facetProfileFields
     * @returns
     */
    public getProfileFieldFacets(
        facetRequest: FacetRequest,
        facetProfileFields: FacetProfileField[]
    ): Observable<ShFacet[]> {
        return this.getFacets(facetRequest).pipe(map(facets => FacetsResponseToShFacets(facets, facetProfileFields)));
    }

    /**
     * Get values of specific ProfileField
     * Constraint options allow you to retrieve extra pages, search for a specific value,...
     *
     * @param query
     * @param facetProfileField
     * @param constraint
     * @returns
     */
    public getProfileFieldFacet(
        query: ApiQuery,
        facetProfileField: FacetProfileField,
        constraint: FacetValueConstraint,
        includeActiveValues: boolean
    ): Observable<ShFacet> {
        const facetRequest: FacetRequest = this.getProfileFieldFacetRequest(
            query,
            facetProfileField,
            constraint,
            includeActiveValues
        );

        return this.getFacets(facetRequest).pipe(
            map(facets => FacetsResponseToShFacets(facets, [facetProfileField])[0])
        );
    }

    // #endregion

    // #region FacetRequest generators
    protected getProfileFacetRequest(query: ApiQuery, profileId: string): FacetRequest {
        const facetRequest: FacetRequest = {
            Search: {
                StructuredQuery: query.toApiStructuredQuery()
            },
            Source: {
                ProfileId: profileId
            }
        };

        return facetRequest;
    }

    protected getProfileFieldFacetRequest(
        query: ApiQuery,
        facetProfileField: FacetProfileField,
        constraint: FacetValueConstraint,
        includeActiveValues: boolean
    ): FacetRequest {
        const options: Record<string, FacetValueOptions> = {};
        options[facetProfileField.DottedKey] = {
            SelectionType: facetProfileField.SelectionType,
            Sort: facetProfileField.Sort,
            ReverseOrder: facetProfileField.ReverseOrder,
            DefaultNumberOfValues: facetProfileField.DefaultNumberOfValues,
            IncludeActiveValues: includeActiveValues,
            IncludeMissingValue: facetProfileField.IncludeMissingValue,
            HideEmpty: facetProfileField.HideEmpty
        };

        const constraints: Record<string, FacetValueConstraint> = {};
        constraints[facetProfileField.DottedKey] = constraint;

        const facetRequest: FacetRequest = {
            Search: {
                StructuredQuery: query.toApiStructuredQuery()
            },
            Source: {
                CustomFacets: [facetProfileField.DottedKey]
            },
            Value: {
                Options: options,
                Constraints: constraints
            }
        };

        return facetRequest;
    }
    // #endregion
}
